import { ENTITY_CLASS, type ProviderKey, ProviderQuery } from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

import { fetchData, findProviders } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

// =============================================================
// プロバイダーの単体取得
// =============================================================

export async function findProvider(providerKey: ProviderKey | false | null | undefined) {
  if (!providerKey) {
    return undefined;
  }
  const { output } = await fetchData({ type: "Entity::Provider", input: { providerKey } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function ProviderQueryKey(providerKey: ProviderKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.PROVIDER, providerKey);
}

export function updateProviderQueryData(
  providerKey: ProviderKey | false | null | undefined,
  newProvider: ReturnType<typeof useProviderQuery>["data"] | false,
) {
  if (providerKey && newProvider !== false && newProvider !== undefined) {
    TANSTACK_QUERY_CLIENT.setQueryData(
      ProviderQueryKey(providerKey),
      function (
        oldProvider: ReturnType<typeof useProviderQuery>["data"],
      ): ReturnType<typeof useProviderQuery>["data"] {
        return !oldProvider ||
          !newProvider ||
          (Temporal.PlainDateTime.compare(oldProvider.createdAt, newProvider.createdAt) ||
            Temporal.PlainDateTime.compare(oldProvider.updatedAt, newProvider.updatedAt)) < 0
          ? newProvider
          : oldProvider;
      },
    );
  }
}

export function useProviderQuery(providerKey: ProviderKey | false | null | undefined) {
  return useQuery({
    queryKey: ProviderQueryKey(providerKey),
    async queryFn() {
      return findProvider(providerKey);
    },

    enabled: Boolean(providerKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

// =============================================================
// プロバイダーの一覧
// =============================================================

function ProviderInfiniteQueryKey(...parameters: Parameters<typeof useProviderInfiniteQuery>) {
  return QueryKey(ENTITY_CLASS.PROVIDER + "List", ...parameters);
}

export function resetProviderInfiniteQueries() {
  TANSTACK_QUERY_CLIENT.resetQueries({ queryKey: QueryKey(ENTITY_CLASS.PROVIDER + "List") });
}

export function useProviderInfiniteQuery(
  query: ProviderQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: ProviderInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { providers, numberOfErrors, nextPageToken } = await findProviders({
          ...query,
          ...context.pageParam,
        });

        for (const provider of providers) {
          updateProviderQueryData(provider.providerKey, provider);
        }

        return { providers, numberOfErrors, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}
